import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { workflowsApi } from '@/api/workflows';
import type { WorkflowInstance, WorkflowInstanceStatus, WorkflowStep } from '@/types/workflows';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { formatDateTime } from '@/utils/format';
import { WorkflowChain, type ChainStepView } from './WorkflowChain';
import { refLabels } from './approver-labels';
import { stepStatuses } from './inbox-grouping';
import styles from '../AdminWorkflowsPage.module.css';

/**
 * One case, told as the same picture the designer drew. The chain renders from
 * the instance's DEFINITION SNAPSHOT — the law the case actually runs under,
 * not whatever version is active today — with decided steps filled in (who,
 * when), the waiting step pulsing, and a taken rejection drawn solid. Below the
 * picture, the decision log with notes, because a rejection note is the maker's
 * only explanation of what to fix.
 *
 * Cancelling lives here rather than on the list rows: it is destructive to the
 * approvers' pending work, so it sits behind the same reasoned confirmation as
 * every other destructive act in the console. The server decides who may cancel;
 * a refusal arrives as its own sentence and is shown verbatim.
 */

const NS = 'admin.workflows';

const STATUS_TONES: Record<WorkflowInstanceStatus, 'info' | 'success' | 'danger' | 'neutral'> = {
  PENDING: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

export function instanceStatusBadge(status: WorkflowInstanceStatus, label: string) {
  return <Badge tone={STATUS_TONES[status]}>{label}</Badge>;
}

interface InstanceDetailModalProps {
  /** null keeps the modal closed; the instance is the open state. */
  instance: WorkflowInstance | null;
  institutionId: string;
  processTitle: string;
  onClose: () => void;
  /** Fired after a successful cancel so the owning list refetches. */
  onChanged: () => void;
}

/** "Any 2 of Principal, Bursar" / "Principal decides" — the step's who-line. */
function approverSummary(
  step: WorkflowStep,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const labels = refLabels(step.approverRefs, []);
  const who = labels.join(', ');
  if (step.approverRefs.length <= 1 && step.quorum <= 1) {
    return t(`${NS}.chain.single`, { who: who || t(`${NS}.chain.unknownApprover`) });
  }
  return t(`${NS}.chain.anyOf`, { quorum: step.quorum, who });
}

export function InstanceDetailModal({
  instance,
  institutionId,
  processTitle,
  onClose,
  onChanged,
}: InstanceDetailModalProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const locale = i18n.language || 'en';

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (!instance) return null;

  const statuses = stepStatuses(instance);
  const chainSteps: ChainStepView[] = instance.definitionSnapshot.steps.map((step, index) => {
    const decisions = instance.decisions.filter((entry) => entry.stepNo === index + 1);
    const last = decisions[decisions.length - 1];
    return {
      title: step.title,
      approverSummary: approverSummary(step, t),
      status: statuses[index],
      detail: last
        ? `${last.decidedByName}${decisions.length > 1 ? ` +${decisions.length - 1}` : ''} · ${formatDateTime(last.at, locale)}`
        : undefined,
    };
  });

  const outcome =
    instance.status === 'APPROVED' || instance.status === 'REJECTED' || instance.status === 'CANCELLED'
      ? instance.status
      : null;

  const cancel = async (reason: string) => {
    setCancelling(true);
    setCancelError(null);
    try {
      // The reason is typed but the cancel endpoint takes none — it exists for
      // the operator's own pause, the same brake every destructive act gets.
      void reason;
      await workflowsApi.member.cancel(institutionId, instance.id);
      toast.success(t(`${NS}.detail.cancelled`));
      setCancelOpen(false);
      onChanged();
    } catch (cause) {
      setCancelError(cause instanceof Error ? cause.message : t('common.actionFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={instance.subjectLabel || t(`${NS}.detail.title`)}
        description={processTitle}
      >
        <div className={styles.detailBody}>
          <dl className={styles.detailFacts}>
            <div className={styles.detailFact}>
              <dt>{t(`${NS}.detail.status`)}</dt>
              <dd>{instanceStatusBadge(instance.status, t(`${NS}.instanceStatus.${instance.status}`))}</dd>
            </div>
            <div className={styles.detailFact}>
              <dt>{t(`${NS}.detail.initiatedBy`)}</dt>
              <dd>{instance.initiatedByName}</dd>
            </div>
            <div className={styles.detailFact}>
              <dt>{t(`${NS}.detail.created`)}</dt>
              <dd>{formatDateTime(instance.createdAt, locale)}</dd>
            </div>
            <div className={styles.detailFact}>
              <dt>{t(`${NS}.detail.resolved`)}</dt>
              <dd>{formatDateTime(instance.resolvedAt, locale)}</dd>
            </div>
          </dl>

          <WorkflowChain
            steps={chainSteps}
            makerLabel={t(`${NS}.chain.makerLabel`)}
            makerDetail={instance.initiatedByName}
            approvedLabel={t(`${NS}.chain.approvedLabel`)}
            rejectedLabel={t(`${NS}.chain.rejectedLabel`)}
            outcome={outcome}
            ariaLabel={t(`${NS}.chain.instanceAria`, { subject: instance.subjectLabel })}
          />

          {instance.decisions.length > 0 && (
            <section>
              <h3 className={styles.detailSectionTitle}>{t(`${NS}.detail.decisions`)}</h3>
              <ul className={styles.decisionList}>
                {instance.decisions.map((decision, index) => (
                  <li key={`${decision.stepNo}-${decision.decidedBy}-${index}`} className={styles.decisionRow}>
                    <div className={styles.decisionHead}>
                      <span className={styles.decisionWho}>{decision.decidedByName}</span>
                      <Badge tone={decision.decision === 'APPROVE' ? 'success' : 'danger'}>
                        {t(`${NS}.detail.${decision.decision === 'APPROVE' ? 'approvedWord' : 'rejectedWord'}`)}
                      </Badge>
                      <span className={styles.decisionWhen}>
                        {t(`${NS}.inbox.stepOf`, {
                          current: decision.stepNo,
                          total: instance.definitionSnapshot.steps.length,
                        })}
                        {' · '}
                        {formatDateTime(decision.at, locale)}
                      </span>
                    </div>
                    {decision.note && <p className={styles.decisionNote}>{decision.note}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className={styles.detailActions}>
            {instance.status === 'PENDING' && (
              <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>
                {t(`${NS}.detail.cancel`)}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmActionDialog
        open={cancelOpen}
        title={t(`${NS}.detail.cancelTitle`)}
        intro={t(`${NS}.detail.cancelIntro`, { subject: instance.subjectLabel })}
        tone="warning"
        impactStatus="ready"
        impact={
          <p className={styles.cancelImpact}>
            {/* Not named `count`: i18next reads that as a plural selector. */}
            {t(`${NS}.detail.cancelImpact`, { n: instance.decisions.length })}
          </p>
        }
        confirmLabel={t(`${NS}.detail.cancel`)}
        submitting={cancelling}
        error={cancelError}
        onCancel={() => setCancelOpen(false)}
        onConfirm={cancel}
      />
    </>
  );
}
